import { prefersReducedMotion } from '../lib/motion-utils'
import {
  trapFocus,
  removeTrapFocus,
  setInertOnSiblings,
  removeInertFromSiblings,
  initMenuKeyboardNav,
  initNavKeyboardNav,
} from '@/lib/a11y'

class HeaderWrapper extends window.HTMLElement {
  connectedCallback() {
    this.header = this
    this.preSpacer = document.querySelector('.pre-spacer')
    this.activeMenuTitle = ''
    this.stickyAnnouncementBar = document.querySelector('.sticky__ab')
    this.announcementPreSpacer = document.querySelector('.pre-spacer__ab')
    this.searchTriggerElement = null
    this.menuKeyboardCleanup = null
    this.navKeyboardCleanup = null

    // check of data-sticky-header exists to init sticky header
    if (this.header.dataset?.stickyHeader !== undefined) {
      this.initSticky()
    }

    this.initDesktopMenuHoverState()
    this.initDesktopMenuKeyboard()
    this.onMenuLeave()
    this.predictiveSearchSetup()

    // set --height-header variable based off .shopify-section-group-header-group heights combined

    const headerGroup = document.querySelectorAll(
      '.shopify-section-group-header-group'
    )

    if (headerGroup) {
      let height = 0
      headerGroup.forEach((node) => {
        height += node.offsetHeight
      })
      document.documentElement.style.setProperty(
        '--height-header',
        `${height}px`
      )
    }
  }

  initSticky() {
    this.headerBounds = {}
    this.currentScrollTop = 0
    this.preventReveal = false

    this.onScrollHandler = this.onScroll.bind(this)
    this.hideHeaderOnScrollUp = () => {
      this.preventReveal = true
    }

    window.addEventListener('scroll', this.onScrollHandler, false)

    this.createObserver()
  }

  onMenuLeave() {
    // when mouse leaves menu, reset active menu title

    this.header.addEventListener('mouseleave', (e) => {
      this.resetMegaMenus(e)
    })

    this.header.querySelectorAll('.mega-menu').forEach((menu) => {
      menu.addEventListener('mouseleave', (e) => {
        this.resetMegaMenus(e)
      })
    })
  }

  onLinkHover(navLinks, link) {
    // add hover links to all navLinks
    navLinks.forEach((link) => {
      link.classList.add('nav-item-hovering')
      // set active menu title - lowercase
    })

    this.activeMenuTitle =
      link.querySelector('span')?.textContent.trim() || link.textContent.trim()

    // attempt to locate mega menu with active menu title
    const megaMenu = document.querySelector(
      `.mega-menu[data-menu-title="${this.activeMenuTitle}"]`
    )

    if (megaMenu) {
      this.preloadMenuImages(megaMenu)
      this.resetMegaMenus()

      const detailsEl = megaMenu.closest('details')
      const summaryEl = detailsEl.querySelector('summary')

      detailsEl.open = true
      detailsEl.setAttribute('aria-expanded', 'true')
      if (summaryEl) {
        summaryEl.setAttribute('aria-expanded', 'true')
      }
    } else {
      this.resetMegaMenus()
    }
  }

  initDesktopMenuHoverState() {
    // attach to nav > a elements to detect hover state

    const navLinks = this.header.querySelectorAll('nav a.top-level-link')

    navLinks.forEach((link) => {
      link.addEventListener('mouseenter', () => {
        this.onLinkHover(navLinks, link)
        const lightning = document.querySelector('lightning-component')
        if (lightning && localStorage.getItem('electrify-mode')) {
          setTimeout(() => {
            lightning.triggerMultipleLightning({
              count: 1,
              lifetime: 70,
            })
          }, 100)
        }
      })

      link.addEventListener('mouseleave', () => {
        // remove hover links to all navLinks
        navLinks.forEach((link) => {
          link.classList.remove('nav-item-hovering')
        })
      })
    })
  }

  initDesktopMenuKeyboard() {
    // Get all mega-menu wrappers with summary triggers
    const megaMenuWrappers = this.header.querySelectorAll(
      'details.mega-menu-wrapper:not(.predictive-search)'
    )

    megaMenuWrappers.forEach((wrapper) => {
      const summary = wrapper.querySelector('summary')
      const megaMenu = wrapper.querySelector('.mega-menu')

      if (!summary || !megaMenu) return

      // Handle keyboard activation on summary (Enter/Space/ArrowDown to open dropdown)
      summary.addEventListener('keydown', (event) => {
        if (event.code === 'Enter' || event.code === 'Space') {
          event.preventDefault()
          if (wrapper.open) {
            this.closeMegaMenu(wrapper, summary)
          } else {
            this.openMegaMenu(wrapper, summary, megaMenu, true)
          }
        } else if (event.code === 'ArrowDown') {
          event.preventDefault()
          this.openMegaMenu(wrapper, summary, megaMenu, true)
        }
      })
    })

    // Initialize left/right arrow navigation between top-level nav items
    // Use specific selector for desktop nav (not mobile menu)
    const nav = this.header.querySelector(
      'nav[aria-labelledby="main-menu-title"]'
    )
    if (nav) {
      this.navKeyboardCleanup = initNavKeyboardNav(
        nav,
        'a.top-level-link, summary'
      )
    }
  }

  /**
   * Preload images in a menu by removing lazy loading
   * @param {HTMLElement} container - Container with images to preload
   */
  preloadMenuImages(container) {
    container.querySelectorAll('img').forEach((img) => {
      img.removeAttribute('loading')
      img.setAttribute('fetchpriority', 'high')
    })
  }

  /**
   * Manage body overflow to prevent scrolling when mega menus are open
   * @param {boolean} hidden - Whether to hide overflow
   */
  setBodyOverflow(hidden) {
    const body = document.body
    if (hidden) {
      body.classList.add('overflow-hidden')
    } else {
      // Only remove overflow if no mega menus (including predictive search) are open
      const anyMegaMenuOpen = document.querySelector('.mega-menu-wrapper[open]')
      if (!anyMegaMenuOpen) {
        body.classList.remove('overflow-hidden')
      }
    }
  }

  toggleMegaMenu(wrapper, summary, megaMenu) {
    if (wrapper.open) {
      this.closeMegaMenu(wrapper, summary)
    } else {
      this.openMegaMenu(wrapper, summary, megaMenu)
    }
  }

  openMegaMenu(wrapper, summary, megaMenu, focusFirstItem = false) {
    this.resetMegaMenus()
    this.preloadMenuImages(megaMenu)
    this.setBodyOverflow(true)

    wrapper.open = true
    wrapper.setAttribute('aria-expanded', 'true')
    summary.setAttribute('aria-expanded', 'true')

    // Initialize keyboard navigation within the menu
    if (this.menuKeyboardCleanup) {
      this.menuKeyboardCleanup()
    }

    this.menuKeyboardCleanup = initMenuKeyboardNav(megaMenu, {
      onClose: () => this.closeMegaMenu(wrapper, summary),
      triggerElement: summary,
    })

    // Focus first menu item if requested (e.g., when opened via ArrowDown)
    if (focusFirstItem) {
      const firstLink = megaMenu.querySelector('a[href]')
      if (firstLink) {
        requestAnimationFrame(() => firstLink.focus())
      }
    }
  }

  closeMegaMenu(wrapper, summary) {
    wrapper.open = false
    wrapper.setAttribute('aria-expanded', 'false')
    summary.setAttribute('aria-expanded', 'false')
    this.setBodyOverflow(false)

    // Cleanup keyboard navigation
    if (this.menuKeyboardCleanup) {
      this.menuKeyboardCleanup()
      this.menuKeyboardCleanup = null
    }
  }

  resetMegaMenus(e) {
    const megaMenus = document.querySelectorAll('.mega-menu-wrapper')

    this.activeMenuTitle = ''

    // Cleanup any existing keyboard navigation
    if (this.menuKeyboardCleanup) {
      this.menuKeyboardCleanup()
      this.menuKeyboardCleanup = null
    }

    megaMenus.forEach((menu) => {
      if (menu.classList && menu.classList.contains('predictive-search')) {
        return
      }

      menu.open = false
      menu.setAttribute('aria-expanded', 'false')

      // Also update aria-expanded on the summary element
      const summary = menu.querySelector('summary')
      if (summary) {
        summary.setAttribute('aria-expanded', 'false')
      }
    })

    this.setBodyOverflow(false)
  }

  createObserver() {
    const observer = new window.IntersectionObserver((entries, observer) => {
      this.headerBounds = entries[0].intersectionRect
      observer.disconnect()
    })

    observer.observe(this.header)
  }

  predictiveSearchSetup() {
    this.predictiveSearchMobile = this.querySelector(
      '.predictive-search.mobile'
    )
    this.predictiveSearchDesktop = this.querySelector(
      '.predictive-search.desktop'
    )
    // listen to open event (details element)
    this.setupPredictiveSearchListeners(this.predictiveSearchMobile)
    this.setupPredictiveSearchListeners(this.predictiveSearchDesktop)
  }

  setupPredictiveSearchListeners(el) {
    // Track click on summary element to capture trigger
    const summary = el.querySelector('summary')
    if (summary) {
      summary.addEventListener('click', () => {
        this.searchTriggerElement = summary
      })
    }

    // Add Escape key listener to close search overlay
    el.addEventListener('keyup', (event) => {
      if (event.code.toUpperCase() === 'ESCAPE' && el.open) {
        el.removeAttribute('open')
        // Toggle event will handle cleanup
      }
    })

    el.addEventListener('toggle', (e) => {
      if (e.target.open) {
        this.header.parentElement.classList.add('search-open')

        // set aria-expanded to true
        el.setAttribute('aria-expanded', 'true')
        // lock scroll
        document.body.style.overflow = 'hidden'

        // Set inert on background content to prevent focus escape
        setInertOnSiblings(el)

        // Focus on search input and set up focus trap
        const searchInput = el.querySelector('.predictive-search-input')
        const predictiveSearchContainer = el.querySelector('predictive-search')

        if (predictiveSearchContainer) {
          trapFocus(
            predictiveSearchContainer,
            searchInput || predictiveSearchContainer
          )
        } else if (searchInput) {
          searchInput.focus()
        }
      } else {
        // set aria-expanded to false
        el.setAttribute('aria-expanded', 'false')

        this.header.parentElement.classList.remove('search-open')

        // unlock scroll
        document.body.style.overflow = ''

        // Remove inert from background and restore focus
        removeInertFromSiblings()
        removeTrapFocus(this.searchTriggerElement)
        this.searchTriggerElement = null
      }
    })
  }

  onScroll() {
    const scrollTop = window.pageYOffset || document.documentElement.scrollTop

    if (this.predictiveSearchMobile && this.predictiveSearchMobile.isOpen)
      return

    if (this.predictiveSearchDesktop && this.predictiveSearchDesktop.isOpen)
      return

    if (
      scrollTop > this.currentScrollTop &&
      scrollTop > this.headerBounds.bottom
    ) {
      if (this.preventHide) return
      window.requestAnimationFrame(this.hide.bind(this))
    } else if (
      scrollTop < this.currentScrollTop &&
      scrollTop > this.headerBounds.bottom
    ) {
      if (!this.preventReveal) {
        window.requestAnimationFrame(this.reveal.bind(this))
      } else {
        window.clearTimeout(this.isScrolling)

        this.isScrolling = setTimeout(() => {
          this.preventReveal = false
        }, 66)

        window.requestAnimationFrame(this.hide.bind(this))
      }
    } else if (scrollTop <= this.headerBounds.top) {
      if (this.stickyAnnouncementBar) {
        if (scrollTop === 0) {
          window.requestAnimationFrame(this.reset.bind(this))
        }
      } else {
        window.requestAnimationFrame(this.reset.bind(this))
      }
    }

    this.currentScrollTop = scrollTop
  }

  hide() {
    const classes = ['-translate-y-full', 'top-0', '!fixed', 'has-scrolled']
    this.preSpacer.style.height = `${this.header.parentElement.offsetHeight}px`
    this.header.parentElement.classList.add(...classes)

    if (this.stickyAnnouncementBar) {
      this.header.parentElement.style.top = `${0}px`

      this.stickyAnnouncementBar.classList.add(
        '-translate-y-full',
        'top-0',
        'has-scrolled'
      )
    }

    document.body.classList.add('header-hidden', 'has-scrolled')
  }

  reveal() {
    this.preSpacer.style.height = `${this.header.parentElement.offsetHeight}px`
    // Only add transition class if user doesn't prefer reduced motion
    if (prefersReducedMotion()) {
      this.header.parentElement.classList.add('top-0')
    } else {
      this.header.parentElement.classList.add('top-0', 'transition-transform')
    }
    this.header.parentElement.classList.remove('-translate-y-full')

    if (this.stickyAnnouncementBar) {
      this.header.parentElement.style.top = `${this.stickyAnnouncementBar.offsetHeight}px`
      if (prefersReducedMotion()) {
        this.stickyAnnouncementBar.classList.add('top-0', '!fixed')
      } else {
        this.stickyAnnouncementBar.classList.add(
          'top-0',
          'transition-transform',
          '!fixed'
        )
      }
      this.stickyAnnouncementBar.classList.remove(
        '-translate-y-full',
        'absolute'
      )
    }

    document.body.classList.remove('header-hidden')
  }

  reset() {
    const classes = [
      '-translate-y-full',
      'top-0',
      'transition-transform',
      '!fixed',
      'has-scrolled',
    ]
    this.preSpacer.style.height = '0'
    this.header.parentElement.classList.remove(...classes)

    if (this.stickyAnnouncementBar) {
      this.header.parentElement.style.top = `${0}px`
      this.header.parentElement.classList.remove('!fixed')

      this.stickyAnnouncementBar.classList.remove(...classes)
      this.stickyAnnouncementBar.classList.add('absolute', 'top-0')
    }
    document.body.classList.remove('has-scrolled')
  }
}

window.customElements.define('header-wrapper', HeaderWrapper)
